// Phase-1 Touchpoints — FE types.
//
// Mirrors the backend ``touchpoints.views`` serializer. Type vocabulary
// is the simple Phase-1 set (``meeting | call | email | other``); the
// model carries v1's finer-grained ``email_in / email_out`` etc., but
// the REST layer normalises to this set so the FE form has fewer
// branches.

export type TouchpointKind = "meeting" | "call" | "email" | "other";


export type TouchpointStatus =
  | "scheduled"
  | "completed"
  | "cancelled"
  | "no_show";


export type TranscriptionStatus =
  | "pending"
  | "transcribing"
  | "succeeded"
  | "failed";


export interface TranscriptSegment {
  /** Start of the segment in seconds, relative to the source
   * recording (chunk offsets already applied server-side). */
  start: number;
  /** End of the segment in seconds, relative to the source. */
  end: number;
  text: string;
}


export interface TouchpointRecording {
  recording_id: string;
  touchpoint_id: string;
  file_id: string;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  duration_seconds: number | null;
  transcription_status: TranscriptionStatus;
  transcript_text: string | null;
  /** Whisper ``verbose_json`` segments. ``null`` for legacy rows
   * transcribed before chunk-3.5 — modal falls back to plain
   * ``transcript_text`` in that case. */
  transcript_segments: TranscriptSegment[] | null;
  transcript_error: string | null;
  transcribed_at: string | null;
  created_at: string | null;
}


export interface TouchpointRecordingListResponse {
  rows: TouchpointRecording[];
}


export interface RecordingPlaybackUrlResponse {
  url: string;
}


export interface Touchpoint {
  touchpoint_id: string;
  account_id: string | null;
  entity_id: string;
  kind: TouchpointKind;
  /** Raw model-level kind (``email_in`` / ``site_visit`` / ...).
   * Useful for migration scenarios; the FE list+modal use ``kind``. */
  kind_raw: string;
  status: TouchpointStatus;
  subject: string;
  body: string;
  outcome_summary: string;
  scheduled_at: string | null;
  occurred_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  duration_minutes: number | null;
  time_zone: string | null;
  participants: string[];
  /** Detail responses include the embedded recordings list so the
   * panel can render without a second fetch. List rows pay the
   * query too at Phase-1 volumes. */
  recordings?: TouchpointRecording[];
  created_at: string | null;
  updated_at: string | null;
  created_by_id: string | null;
  updated_by_id: string | null;
}


export interface TouchpointListResponse {
  rows: Touchpoint[];
  total: number;
}


export interface TouchpointCreateInput {
  kind: TouchpointKind;
  subject?: string | null;
  scheduled_at?: string | null;
  occurred_at?: string | null;
  duration_minutes?: number | null;
  time_zone?: string | null;
  notes?: string | null;
  outcome_summary?: string | null;
  status?: TouchpointStatus | null;
  attendees?: string[];
}


export type TouchpointUpdateInput = Partial<TouchpointCreateInput>;


export interface TouchpointStatsResponse {
  window_days: number;
  upcoming: number;
  overdue: number;
  completed_recent: number;
  cancelled_recent: number;
  no_show_recent: number;
  by_kind_recent: Record<TouchpointKind, number>;
}


export const TOUCHPOINT_KIND_LABEL: Record<TouchpointKind, string> = {
  meeting: "Meeting",
  call: "Call",
  email: "Email",
  other: "Other",
};


export const TOUCHPOINT_STATUS_LABEL: Record<TouchpointStatus, string> = {
  scheduled: "Scheduled",
  completed: "Completed",
  cancelled: "Cancelled",
  no_show: "No-show",
};
